/**
 * Expanded pricing-rule view — inspector-style detail sheet consistent with
 * the Inventory / Gift cards index pages (docs/expandable-index-pages.md):
 * identity header with actions, key facts, then Scope / Facet match sections.
 * Used by both the Price Rules and Condition Discounts tabs.
 */
import { useState } from 'react';
import {
  Confirm,
  useCreatePath,
  useDelete,
  useGetList,
  useGetOne,
  useListContext,
  useNotify,
  useRecordContext,
  useRefresh,
  useResourceContext,
} from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';

import { CopyButton, DetailRow, KeyFactsStrip, SectionLabel } from '@src/components/indexPage';
import { getCurrencySymbol } from '@src/utils/currency';
import type { PartnerSettings } from '@src/settings';

import type { ConditionOption } from '@src/conditions';

import { facetPairs, formatRuleDiscount, ruleCriteriaCount, ruleScopeSummary, sourceColumnLabel } from './ruleFormat';
import type { PricingRule, PricingType } from '../pricingRules/types';

interface RuleExpandProps {
  pricingType: PricingType;
}

const RuleExpand = ({ pricingType }: RuleExpandProps) => {
  const rule = useRecordContext<PricingRule>();
  const resource = useResourceContext();
  const createPath = useCreatePath();
  const notify = useNotify();
  const refresh = useRefresh();
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });
  const isCondition = pricingType === 'condition';
  const { data: conditions = [] } = useGetList<ConditionOption>('conditions', {}, { enabled: isCondition });

  if (!rule || !resource) return null;

  const currencySymbol = getCurrencySymbol(settings?.primaryCurrencyCode);
  const conditionLabels = Object.fromEntries(conditions.map(({ code, label }) => [code, label]));
  const pairs = facetPairs(rule);

  // Apply a filter from the detail sheet: set the value(s) and mark the
  // matching filter input displayed so react-admin keeps and shows it —
  // setting a value alone leaves a non-displayed filter looking unapplied.
  const applyFilter = (patch: Record<string, string>, show: string[]) => {
    setFilters(
      { ...filterValues, ...patch },
      { ...displayedFilters, ...Object.fromEntries(show.map(source => [source, true])) },
    );
  };

  const filterLink = (field: string, value: string | null | undefined) =>
    (value ? () => applyFilter({ [field]: value }, [field]) : undefined);

  // A facet is a tag + value pair. Filter on both at once so the dependent
  // value isn't cleared by the tag-less guard, shown under the "Facet" filter.
  const facetLink = (tag: string | null | undefined, value: string | null | undefined) =>
    (tag && value ? () => applyFilter({ tag, name: value }, ['tag']) : undefined);

  const handleDelete = () => {
    void deleteOne(
      resource,
      { id: rule.id, previousData: rule },
      {
        onSuccess: () => {
          notify(`Rule #${rule.id} deleted`, { type: 'success' });
          setConfirmOpen(false);
          refresh();
        },
        onError: (error) => {
          notify(error instanceof Error ? error.message : 'Failed to delete rule', { type: 'error' });
          setConfirmOpen(false);
        },
      },
    );
  };

  return (
    <Box sx={{ bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', p: 2.5 }}>
      <Stack spacing={2}>
        {/* Identity header + actions */}
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontSize: 16, lineHeight: 1.3 }}>
                {ruleScopeSummary(rule, pricingType, conditionLabels)}
              </Typography>
              <Chip size="small" variant="outlined" label={`Priority ${rule.position + 1}`} />
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'inline-flex', alignItems: 'center' }}>
              {`Rule #${rule.id}`}
              <CopyButton name="Rule ID" value={String(rule.id)} />
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              component={RouterLink}
              to={createPath({ resource, id: rule.id, type: 'edit' })}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmOpen(true)}
            >
              Delete
            </Button>
          </Stack>
        </Stack>

        <KeyFactsStrip
          facts={[
            { label: 'Priority', value: rule.position + 1 },
            ...(isCondition ?
              [{ label: 'Condition', value: rule.condition ? conditionLabels[rule.condition] ?? rule.condition : 'All' }] :
              [{ label: 'Source price', value: sourceColumnLabel(rule.sourceColumn) }]),
            {
              label: rule.sourceColumn === 'absolute_price' ? 'Fixed price' : 'Discount',
              value: formatRuleDiscount(rule, currencySymbol),
            },
            { label: 'Criteria', value: ruleCriteriaCount(rule, pricingType) || 'None (matches all)' },
          ]}
        />

        {/* Detail sections */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(250px, 1fr))' },
            columnGap: 4,
            rowGap: 2.5,
            alignContent: 'start',
          }}
        >
          <Box>
            <SectionLabel>Scope</SectionLabel>
            {isCondition && (
              <DetailRow
                label="Condition"
                value={rule.condition ? conditionLabels[rule.condition] ?? rule.condition : undefined}
                onClick={filterLink('condition', rule.condition)}
              />
            )}
            <DetailRow label="Department" value={rule.department} onClick={filterLink('department', rule.department)} />
            <DetailRow label="Category" value={rule.category} onClick={filterLink('category', rule.category)} />
            <DetailRow label="Merchant category" value={rule.merchantCategory} onClick={filterLink('merchantCategory', rule.merchantCategory)} />
            {!isCondition && <DetailRow label="UPC" value={rule.upc} mono />}
          </Box>
          <Box>
            <SectionLabel>Facet match</SectionLabel>
            {pairs.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: 13 }}>
                No facet criteria — matches every item in scope.
              </Typography>
            )}
            {rule.tag && <DetailRow label={`Primary · ${rule.tag}`} value={rule.name ?? 'Any value'} onClick={facetLink(rule.tag, rule.name)} />}
            {rule.secondaryTag && <DetailRow label={`Secondary · ${rule.secondaryTag}`} value={rule.secondaryName ?? 'Any value'} />}
            {rule.tertiaryTag && <DetailRow label={`Tertiary · ${rule.tertiaryTag}`} value={rule.tertiaryName ?? 'Any value'} />}
          </Box>
          <Box>
            <SectionLabel>Effect</SectionLabel>
            <DetailRow
              label="Adjustment"
              value={
                rule.sourceColumn === 'absolute_price' ?
                  `Sets the resale price to ${formatRuleDiscount(rule, currencySymbol)}` :
                  isCondition ?
                    `${rule.discount ?? 0}% off the calculated resale price` :
                    `Resale price = ${rule.discount ?? 0}% of ${sourceColumnLabel(rule.sourceColumn)}`
              }
            />
            <DetailRow
              label="Evaluation"
              value={`Checked ${rule.position === 0 ? 'first' : `after ${rule.position} higher rule${rule.position === 1 ? '' : 's'}`} — first match wins`}
            />
          </Box>
        </Box>
      </Stack>

      <Confirm
        isOpen={confirmOpen}
        loading={isDeleting}
        title={`Delete rule #${rule.id}?`}
        content="Items matched by this rule will fall through to the next rule in priority order. Remember to apply changes afterwards."
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </Box>
  );
};

export default RuleExpand;
